import { CampaignDisplayStatus, CampaignSummary } from "@signalist/shared-types";

import { canStartInBulk } from "@/pages/Private/pages/List/components/campaignBulkEligibility";

/** A campaign a send-path assign is about to put leads into, which is not sending yet. */
export interface CampaignToStart {
	campaignId: string;
	name: string;
	status: CampaignDisplayStatus;
	/** Leads already assigned to the campaign — starting it contacts them too, not only the new ones. */
	assignedLeads: number;
}

/**
 * Which of the campaigns an assign targets would have to be started for the assigned leads to be
 * contacted (PREC-3296).
 *
 * Starting is **per campaign**. The list-level `campaignStatus` is a rollup: writing RUNNING to it
 * fans out to every campaign on the list that is not individually paused, so a dormant sibling
 * starts mailing its own leads along with the one the user picked. The per-campaign status is the
 * send gate, and it is the one asked about here.
 *
 * "Needs starting" is exactly what the Campaigns column's "Activate all" starts — the same
 * {@link canStartInBulk} classification — so the prompt and that control never disagree about a
 * status. ACTIVE and LOADING (already starting) need nothing; INVALID and ENDED cannot be started
 * and are refused upstream (the picker disables them, the bulk assign rejects them), so offering to
 * start one would be a promise the button cannot keep.
 */
export const resolveCampaignsToStart = (
	summaries: CampaignSummary[],
	campaignIds: string[]
): CampaignToStart[] => {
	const wanted = new Set(campaignIds.filter(Boolean));

	return summaries
		.filter((summary) => wanted.has(summary.campaignId) && canStartInBulk(summary.status))
		.map((summary) => ({
			campaignId: summary.campaignId,
			name: summary.name,
			status: summary.status,
			assignedLeads: summary.total,
		}));
};

/** A campaign the user chose to start whose start request was refused, with the API's reason. */
export interface CampaignStartFailure {
	name: string;
	reason: string;
}

/**
 * The API's own reason for a refused start (a string, or a class-validator array), else `fallback`.
 * Shown verbatim in the modal: a start that failed must say why, because nothing else will —
 * `rtkRejectQueryMiddleware` stays silent on a rejected mutation (PREC-4129).
 */
export const describeStartFailure = (error: unknown, fallback: string): string => {
	const message =
		typeof error === "object" && error !== null
			? (error as { data?: { message?: unknown } }).data?.message
			: undefined;

	if (Array.isArray(message)) {
		return message.join(", ");
	}

	return typeof message === "string" && message.length > 0 ? message : fallback;
};

/** How a campaign's own run state reads to the user, and whether it is sending. */
export interface CampaignStateLabel {
	/** Translation key under `outreachActivation.campaignState`. */
	key: string;
	sending: boolean;
}

/**
 * The label for a campaign's run state — total over `CampaignDisplayStatus` (a `switch` with no
 * `default`), so a new status fails the build here instead of reaching the screen as a raw key
 * (web CLAUDE.md §10). The Review-to-send picker used to label every campaign from the list-wide
 * rollup, so a never-started campaign on a running list read "Active" (PREC-3296).
 */
export const campaignStateLabel = (status: CampaignDisplayStatus): CampaignStateLabel => {
	switch (status) {
		case CampaignDisplayStatus.ACTIVE:
			return { key: "outreachActivation.campaignState.active", sending: true };
		case CampaignDisplayStatus.LOADING:
			return { key: "outreachActivation.campaignState.starting", sending: true };
		case CampaignDisplayStatus.READY:
		case CampaignDisplayStatus.VALID:
			return { key: "outreachActivation.campaignState.notStarted", sending: false };
		case CampaignDisplayStatus.PAUSED:
			return { key: "outreachActivation.campaignState.paused", sending: false };
		case CampaignDisplayStatus.ENDED:
			return { key: "outreachActivation.campaignState.ended", sending: false };
		case CampaignDisplayStatus.INVALID:
			return { key: "outreachActivation.campaignState.invalid", sending: false };
	}
};
